'use client';

import cx from 'classnames';
import { useEffect, useState } from 'react';

import { Button } from '../form/button/Button';
import { IconClose } from '../icons/material/rounded/Close';
import { IconInfo } from '../icons/material/rounded/Info';
import { Typography } from '../typography/Typography';

import style from './Popin.module.css';

const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

function readDismissedUntil(storageKey: string): number {
    const raw = localStorage.getItem(storageKey);
    const until = Number(raw);

    return raw === null || Number.isNaN(until) ? 0 : until;
}

type Variant = 'positive' | 'neutral';

type Props = {
    variant: Variant;
    title: string;
    message: string;
    closeLabel: string;
    pillLabel?: string | null;
    ctaLabel?: string | null;
    ctaUrl?: string | null;
    className?: string;
    storageKey?: string;
};

export function Popin({
    variant,
    title,
    message,
    closeLabel,
    pillLabel,
    ctaLabel,
    ctaUrl,
    className,
    storageKey,
}: Props) {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (storageKey === undefined || readDismissedUntil(storageKey) <= Date.now()) {
            setIsOpen(true);
        }
    }, [storageKey]);

    const close = () => {
        if (storageKey !== undefined) {
            localStorage.setItem(storageKey, String(Date.now() + DISMISS_DURATION_MS));
        }
        setIsOpen(false);
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className={cx(style.popin, className, {
                [style.positive]: variant === 'positive',
                [style.neutral]: variant === 'neutral',
            })}
        >
            <button
                type="button"
                className={style.closeButton}
                aria-label={closeLabel}
                onClick={close}
            >
                <IconClose className={style.closeIcon} />
            </button>

            <div className={style.content}>
                {pillLabel ? (
                    <Typography scale="primary-xs-bold" className={style.pill}>
                        {variant === 'neutral' ? (
                            <IconInfo className={style.pillIcon} />
                        ) : (
                            <span aria-hidden="true" className={style.pillDot} />
                        )}
                        <span>{pillLabel}</span>
                    </Typography>
                ) : null}
                <Typography as="p" scale="secondary-s-regular" className={style.title}>
                    {title}
                </Typography>
                <Typography as="p" scale="primary-body-regular" className={style.message}>
                    {message}
                </Typography>
                {ctaLabel && ctaUrl ? (
                    <div className={style.cta}>
                        <Button
                            priority="secondary"
                            reversed={variant === 'positive'}
                            href={ctaUrl}
                        >
                            {ctaLabel}
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
